import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { Activity, BarChart3, Brain, CheckCircle2, Gauge, Radio, Shield, TrendingDown, TrendingUp, Zap } from "lucide-react";
import type { MarketScan, TradingSignal } from "./lib/types";
import "./styles.css";

type Page = "home" | "dashboard" | "about" | "status";

const nav: Array<{ id: Page; label: string }> = [
  { id: "home", label: "Home" },
  { id: "dashboard", label: "Dashboard" },
  { id: "about", label: "About" },
  { id: "status", label: "Status" }
];

function formatValue(value: number | null): string {
  if (value === null) return "-";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

function useMarketScan() {
  const [scan, setScan] = useState<MarketScan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const response = await fetch("/api/analyze");
      const payload = await response.json() as unknown;
      if (!response.ok) {
        const errorPayload = payload as { error?: string };
        throw new Error(errorPayload.error || "Analysis failed");
      }
      setScan(payload as MarketScan);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Analysis failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 300000);
    return () => window.clearInterval(timer);
  }, []);

  return { scan, error, loading, reload: load };
}

function Header({ page, setPage }: { page: Page; setPage: (page: Page) => void }) {
  return (
    <header className="topbar">
      <button className="brand" onClick={() => setPage("home")} aria-label="Youdinix AI home">
        <span className="brand-mark"><Brain size={20} /></span>
        <span>Youdinix AI</span>
      </button>
      <nav>
        {nav.map((item) => (
          <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => setPage(item.id)}>
            {item.label}
          </button>
        ))}
      </nav>
    </header>
  );
}

function SignalCard({ signal }: { signal: TradingSignal | null }) {
  return (
    <section className="signal-card">
      <div className="signal-heading">
        <span>Highest probability opportunity</span>
        <strong className={signal?.direction.toLowerCase() || "wait"}>{signal?.direction || "LOADING"}</strong>
      </div>
      <div className="signal-grid">
        <div><span>Coin</span><strong>{signal?.coin || "-"}</strong></div>
        <div><span>Confidence</span><strong>{signal ? `${signal.confidence}%` : "-"}</strong></div>
        <div><span>Entry</span><strong>{formatValue(signal?.entry ?? null)}</strong></div>
        <div><span>Stop Loss</span><strong>{formatValue(signal?.stopLoss ?? null)}</strong></div>
        <div><span>Take Profit</span><strong>{formatValue(signal?.takeProfit ?? null)}</strong></div>
        <div><span>Risk</span><strong>{signal?.riskLevel || "-"}</strong></div>
      </div>
      <p>{signal?.shortReason || "Loading live market confluence."}</p>
    </section>
  );
}

function Hero({ scan, setPage }: { scan: MarketScan | null; setPage: (page: Page) => void }) {
  return (
    <section className="hero">
      <div className="hero-copy">
        <h1>Youdinix AI</h1>
        <p>
          A 24/7 crypto market intelligence system that scans live structure, momentum,
          volatility, historical behavior, and sentiment before sending selective Discord webhook alerts.
        </p>
        <div className="hero-actions">
          <button onClick={() => setPage("dashboard")}><BarChart3 size={18} /> Open Dashboard</button>
          <button className="ghost" onClick={() => setPage("status")}><Radio size={18} /> System Status</button>
        </div>
      </div>
      <div className="hero-visual">
        <div className="chart-glass">
          <div className="chart-top">
            <span>{scan?.trend || "Scanning market"}</span>
            <strong>{scan?.sentiment || "Live"}</strong>
          </div>
          <div className="bars" aria-hidden="true">
            {Array.from({ length: 34 }, (_, index) => (
              <i key={index} style={{ height: `${28 + Math.abs(Math.sin(index * 0.72)) * 62}%` }} />
            ))}
          </div>
          <SignalCard signal={scan?.highestProbabilityOpportunity || null} />
        </div>
      </div>
    </section>
  );
}

function Dashboard({ scan, loading, error, reload }: { scan: MarketScan | null; loading: boolean; error: string | null; reload: () => void }) {
  const opportunities = useMemo(() => scan?.opportunities || [], [scan]);
  return (
    <section className="page dashboard">
      <div className="section-title">
        <div>
          <h2>Live Market Dashboard</h2>
          <p>Data refreshes from the Cloudflare Pages API and live exchange candles.</p>
        </div>
        <button onClick={reload} disabled={loading}><Activity size={18} /> {loading ? "Scanning" : "Refresh"}</button>
      </div>
      {error ? <div className="notice">{error}</div> : null}
      <div className="dashboard-grid">
        <SignalCard signal={scan?.highestProbabilityOpportunity || null} />
        <div className="metric-board">
          <article><Gauge size={20} /><span>Trend</span><strong>{scan?.trend || "-"}</strong></article>
          <article><TrendingUp size={20} /><span>Most Bullish</span><strong>{scan?.mostBullishCoin || "-"}</strong></article>
          <article><TrendingDown size={20} /><span>Most Bearish</span><strong>{scan?.mostBearishCoin || "-"}</strong></article>
          <article><Zap size={20} /><span>Fear / Greed</span><strong>{scan?.fearGreed || "-"}</strong></article>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Coin</th>
              <th>Direction</th>
              <th>Confidence</th>
              <th>Risk</th>
              <th>Trend</th>
              <th>Momentum</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((item) => (
              <tr key={item.coin}>
                <td>{item.coin}</td>
                <td><span className={`tag ${item.direction.toLowerCase()}`}>{item.direction}</span></td>
                <td>{item.confidence}%</td>
                <td>{item.riskLevel}</td>
                <td>{item.indicators.trend}</td>
                <td>{formatValue(item.indicators.momentum)}</td>
                <td>{item.shortReason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="page split">
      <div>
        <h2>Built for Selective Signals</h2>
        <p>
          Youdinix AI evaluates technical confluence, market sentiment, volatility,
          trend behavior, and historical similarity before allowing a signal through.
          The system is intentionally conservative and avoids webhook spam.
        </p>
      </div>
      <div className="principles">
        <article><Shield size={20} /><strong>No Discord bot</strong><span>Webhook-only delivery.</span></article>
        <article><CheckCircle2 size={20} /><strong>Confidence gate</strong><span>Signals require at least 75% confidence.</span></article>
        <article><Brain size={20} /><strong>Adaptive review</strong><span>Historical outcomes influence probability.</span></article>
      </div>
    </section>
  );
}

function Status({ scan, error }: { scan: MarketScan | null; error: string | null }) {
  return (
    <section className="page status">
      <h2>System Status</h2>
      <div className="status-grid">
        <article><span>Website</span><strong>Online</strong></article>
        <article><span>Pages API</span><strong>{error ? "Degraded" : "Online"}</strong></article>
        <article><span>Last scan</span><strong>{scan ? new Date(scan.generatedAt).toLocaleString() : "Pending"}</strong></article>
        <article><span>Founder</span><strong>Yousif A.</strong></article>
      </div>
    </section>
  );
}

function App() {
  const [page, setPage] = useState<Page>("home");
  const market = useMarketScan();
  return (
    <>
      <Header page={page} setPage={setPage} />
      <main>
        {page === "home" ? <Hero scan={market.scan} setPage={setPage} /> : null}
        {page === "dashboard" ? <Dashboard {...market} /> : null}
        {page === "about" ? <About /> : null}
        {page === "status" ? <Status scan={market.scan} error={market.error} /> : null}
      </main>
      <footer>
        <span>Founder: Yousif A.</span>
        <span>Youdinix AI</span>
      </footer>
    </>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
